import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { trackEvent } from "./track";
import { MapPin, Phone, Send, Loader2, ExternalLink } from "lucide-react";
import { useI18n } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAP_DIRECTIONS_URL = "https://www.google.com/maps/search/?api=1&query=2828+boulevard+Laurier+suite+700+Qu%C3%A9bec+QC+G1V+0B9";

export const Contact = () => {
  const { t, lang } = useI18n();
  const [form, setForm] = useState({ name: "", email: "", phone: "", solution: "", message: "" });
  const [simulation, setSimulation] = useState(null);
  const [sending, setSending] = useState(false);
  const [mapFailed, setMapFailed] = useState(false);
  const mapLoadedRef = useRef(false);
  const mapBoxRef = useRef(null);

  useEffect(() => {
    const box = mapBoxRef.current;
    if (!box) return;
    let timer;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        obs.disconnect();
        const probe = new Image();
        probe.onload = () => { mapLoadedRef.current = true; };
        probe.onerror = () => setMapFailed(true);
        probe.src = "https://maps.gstatic.com/mapfiles/api-3/images/spotlight-poi2.png?" + Date.now();
        timer = setTimeout(() => {
          if (!mapLoadedRef.current) setMapFailed(true);
        }, 10000);
      }
    }, { threshold: 0.1 });
    obs.observe(box);
    return () => {
      obs.disconnect();
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    const onPrefill = (e) => setForm((f) => ({ ...f, solution: e.detail }));
    const onSim = (e) => {
      const { message, ...sim } = e.detail;
      setSimulation(sim);
      setForm((f) => ({ ...f, message: f.message.trim() ? f.message : message }));
    };
    window.addEventListener("prefill-solution", onPrefill);
    window.addEventListener("prefill-simulation", onSim);
    return () => {
      window.removeEventListener("prefill-solution", onPrefill);
      window.removeEventListener("prefill-simulation", onSim);
    };
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    if (form.name.trim().length < 2) {
      toast.error(t("contact.errName"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error(t("contact.errEmail"));
      return;
    }
    if (form.phone.replace(/\D/g, "").length < 7) {
      toast.error(t("contact.errPhone"));
      return;
    }
    setSending(true);
    try {
      const solutionTag = form.solution ? `[Solution recherchée : ${form.solution}]` : "";
      const question = form.message.trim();
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone,
        message: [solutionTag, question].filter(Boolean).join("\n"),
        language: lang,
        calculator_used: !!simulation,
        simulation: simulation || null,
      };
      await axios.post(`${API}/contact`, payload);
      trackEvent("form_submit", { form_name: "contact", solution: form.solution || "non précisée", language: lang, calculator_used: !!simulation });
      toast.success(t("contact.success"));
      setForm({ name: "", email: "", phone: "", solution: "", message: "" });
      setSimulation(null);
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error(t("contact.rateLimited"));
      } else {
        toast.error(t("contact.error"));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-36 border-t border-white/10" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-5"
        >
          <p className="font-heading text-gold text-sm tracking-[0.25em] uppercase mb-6">{t("contact.kicker")}</p>
          <h2 className="font-heading font-bold tracking-tighter text-3xl sm:text-4xl lg:text-5xl mb-8">
            {t("contact.heading")}
          </h2>
          <p className="text-[#8A9DB1] leading-relaxed text-base md:text-lg mb-12 max-w-md">{t("contact.sub")}</p>

          <div className="space-y-8">
            <div className="flex items-start gap-4" data-testid="contact-address">
              <MapPin size={20} className="text-gold mt-1 shrink-0" strokeWidth={1.6} />
              <p className="text-[#c8d3dc] leading-relaxed">
                {t("address.line1")}<br />
                {t("address.line2")}<br />
                {t("address.country")}
              </p>
            </div>
            <div className="flex items-start gap-4" data-testid="contact-phones">
              <Phone size={20} className="text-gold mt-1 shrink-0" strokeWidth={1.6} />
              <div className="text-[#c8d3dc] leading-relaxed">
                <a href="tel:+15813333447" className="block hover:text-white transition-colors duration-300" data-testid="phone-link-1">
                  +1 581-333-3447
                </a>
              </div>
            </div>
          </div>

          <div className="mt-12" data-testid="contact-map">
            <div ref={mapBoxRef} className="relative border border-white/10 overflow-hidden" style={{ height: "260px" }}>
              {mapFailed ? (
                <div className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center bg-white/[0.03]" data-testid="contact-map-fallback">
                  <MapPin size={26} className="text-gold" strokeWidth={1.6} />
                  <p className="text-[#c8d3dc] leading-relaxed text-sm">
                    {t("address.line1")}<br />{t("address.line2")}, {t("address.country")}
                  </p>
                  <a
                    href={MAP_DIRECTIONS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 text-xs font-heading tracking-[0.12em] uppercase"
                    data-testid="contact-map-fallback-link"
                  >
                    {t("contact.mapLink")} <ExternalLink size={13} />
                  </a>
                </div>
              ) : (
                <iframe
                  title={t("contact.mapTitle")}
                  src={`https://www.google.com/maps?q=2828+boulevard+Laurier,+suite+700,+Qu%C3%A9bec,+QC+G1V+0B9&z=15&hl=${lang}&output=embed`}
                  className="w-full h-full border-0"
                  style={{ filter: "grayscale(0.85) invert(0.92) hue-rotate(185deg) brightness(0.9) contrast(0.92)" }}
                  loading="lazy"
                  data-testid="contact-map-iframe"
                />
              )}
              <div className="absolute inset-0 pointer-events-none border border-[#E8A33D]/20" />
            </div>
            {!mapFailed && (
              <a
                href={MAP_DIRECTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-4 text-gold font-heading text-sm tracking-[0.12em] uppercase group"
                data-testid="contact-map-link"
              >
                {t("contact.mapLink")}
                <ExternalLink size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            )}
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.15 }}
          onSubmit={submit}
          className="lg:col-span-7 lg:pl-8 space-y-6"
          data-testid="contact-form"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="cf-name" className="block font-heading text-sm tracking-[0.12em] uppercase text-[#8A9DB1] mb-3">
                {t("contact.nameLabel")}
              </label>
              <input
                id="cf-name"
                type="text"
                value={form.name}
                onChange={set("name")}
                placeholder={t("contact.namePh")}
                className="cs-input w-full px-5 py-4 text-base"
                data-testid="contact-name-input"
              />
            </div>
            <div>
              <label htmlFor="cf-email" className="block font-heading text-sm tracking-[0.12em] uppercase text-[#8A9DB1] mb-3">
                {t("contact.emailLabel")}
              </label>
              <input
                id="cf-email"
                type="email"
                value={form.email}
                onChange={set("email")}
                placeholder={t("contact.emailPh")}
                className="cs-input w-full px-5 py-4 text-base"
                data-testid="contact-email-input"
              />
            </div>
          </div>
          <div>
            <label htmlFor="cf-phone" className="block font-heading text-sm tracking-[0.12em] uppercase text-[#8A9DB1] mb-3">
              {t("contact.phoneLabel")}
            </label>
            <input
              id="cf-phone"
              type="tel"
              value={form.phone}
              onChange={set("phone")}
              placeholder={t("contact.phonePh")}
              className="cs-input w-full px-5 py-4 text-base"
              data-testid="contact-phone-input"
            />
          </div>
          <div>
            <label htmlFor="cf-solution" className="block font-heading text-sm tracking-[0.12em] uppercase text-[#8A9DB1] mb-3">
              {t("contact.solutionLabel")} <span className="normal-case tracking-normal">{t("contact.solutionOptional")}</span>
            </label>
            <select
              id="cf-solution"
              value={form.solution}
              onChange={(e) => {
                const v = e.target.value;
                setForm((f) => ({ ...f, solution: v }));
                if (v) trackEvent("solution_selected", { solution: v, language: lang });
              }}
              className="cs-input w-full px-5 py-4 text-base appearance-none cursor-pointer"
              data-testid="contact-solution-select"
            >
              <option value="">{t("contact.solutionSelectPh")}</option>
              {form.solution && !t("solutions.items").some((s) => s.title === form.solution) && (
                <option value={form.solution}>{form.solution}</option>
              )}
              {t("solutions.items").map((s) => (
                <option key={s.title} value={s.title}>
                  {s.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="cf-message" className="block font-heading text-sm tracking-[0.12em] uppercase text-[#8A9DB1] mb-3">
              {t("contact.messageLabel")} <span className="normal-case tracking-normal">{t("contact.solutionOptional")}</span>
            </label>
            <textarea
              id="cf-message"
              rows={6}
              value={form.message}
              onChange={set("message")}
              placeholder={t("contact.messagePh")}
              className="cs-input w-full px-5 py-4 text-base resize-none"
              data-testid="contact-message-input"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            className="btn-gold inline-flex items-center justify-center gap-2 px-10 py-4 text-base disabled:opacity-60"
            data-testid="contact-submit-button"
          >
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {sending ? t("contact.sending") : t("contact.submit")}
          </button>
          <p className="text-[#8A9DB1] text-xs leading-relaxed max-w-lg" data-testid="contact-consent-note">
            {t("contact.consentNote")}
          </p>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
